import { DragEvent, useState } from 'react';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { useIntl } from 'react-intl';
import { ImageFile } from 'ui-widgets/components/Modals/UploadImageModal/UploadImageModal.types';
import { Base64File, uploadSearchImage } from '../api/imageSearch';
import { convertFileToUploadFormat } from '../helpers';
import { definedTranslation } from '../i18n';

export function useVisualSearch() {
  const intl = useIntl();

  const [isImageSearchOpened, setIsImageSearchOpened] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const visualSearchModal = {
    title: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.title,
    ),
    subtitle: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.subtitle,
    ),
    dragTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.dropzoneText,
    ),
    uploadButtonLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.dropzoneButton,
    ),
    orTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.or,
    ),
    searchInputPlaceholder: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.placeholder,
    ),
    searchInputButton: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.button,
    ),
    uploadingTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.uploading,
    ),
    wrongFormatErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.wrongType,
    ),
    tooManyFilesErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.tooManyFiles,
    ),
    imageNotFoundErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.imageNotFound,
    ),
    someErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.someError,
    ),
  };

  const handleImageSearchFormSubmit = async (value: string) => {
    setUploadErrors([]);
    setIsUploadLoading(true);
    const data = await uploadSearchImage(value);
    setIsUploadLoading(false);
    if (data.item != null) {
      setIsImageSearchOpened(false);
      window.dispatchEvent(
        new CustomEvent('initVisualSearch', {
          detail: { item: data.item },
        }),
      );
    } else {
      setUploadErrors([
        data?.errorMessage || visualSearchModal.imageNotFoundErrorLabel,
      ]);
    }
  };

  const handleUploadFile = async (file: Base64File) => {
    const data = await uploadSearchImage(null, file);
    setIsUploadLoading(false);
    if (data.item != null) {
      setIsImageSearchOpened(false);
      window.dispatchEvent(
        new CustomEvent('initVisualSearch', {
          detail: { item: data.item },
        }),
      );
    } else {
      setUploadErrors([data?.errorMessage || visualSearchModal.someErrorLabel]);
    }
  };

  const handleImageDrop = async (imageFiles: ImageFile[]) => {
    if (imageFiles.length === 0) {
      return;
    }
    setUploadErrors([]);
    setIsUploadLoading(true);
    const imageFile = await convertFileToUploadFormat(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      imageFiles?.[0] as unknown as File,
    );
    handleUploadFile(imageFile);
  };

  const handleImageSearchClick = () => {
    setIsImageSearchOpened(true);
  };

  const handleModalClose = () => {
    setUploadErrors([]);
    setIsImageSearchOpened(false);
  };

  const handleDragOver = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  const handleOnDrop = async (ev: DragEvent) => {
    try {
      ev.preventDefault();
      let file = undefined;
      if (ev.dataTransfer.items) {
        const itemFile = Array.from(ev.dataTransfer.items)?.[0];
        if (itemFile.kind === 'file') {
          file = itemFile.getAsFile();
        }
      } else {
        file = Array.from(ev.dataTransfer.files)?.[0];
      }
      if (file != null) {
        setIsImageSearchOpened(true);
        setUploadErrors([]);
        setIsUploadLoading(true);
        const base64 = await convertFileToUploadFormat(file);
        handleUploadFile(base64);
      }
    } catch (e) {
      handleSystemErrorReport(
        `Error in SearchBar handleOnDrop: ${e.message}`,
        'handleOnDrop',
      );
    }
  };

  return {
    isImageSearchOpened,
    isUploadLoading,
    uploadErrors,
    visualSearchModal,
    handleModalClose,
    handleImageSearchClick,
    handleImageSearchFormSubmit,
    handleImageDrop,
    handleDragOver,
    handleOnDrop,
  };
}
